import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
  Image,
} from "@chakra-ui/react";
import { FormEvent, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { colors } from "../colors";
import { useShowToast } from "../hooks/useShowToast";
import { useAuth } from "../hooks/useAuth";
import skipgiveLogo from "../assets/skipgive-logo.png";
import supabase from "../supabase";

export const ResetPassword = () => {
  const { updatePassword } = useAuth();
  const { showToast } = useShowToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);

  useEffect(() => {
    // Check if there's a valid recovery session
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setIsValidSession(true);
      } else {
        showToast("Invalid or expired reset link", "error");
        navigate("/");
      }
    };
    checkSession();
  }, [navigate, showToast]);

  const handleResetPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsProcessing(true);

    if (!password || !confirmPassword) {
      showToast("Please fill in all fields!", "warning");
      setIsProcessing(false);
      return;
    }

    if (password !== confirmPassword) {
      showToast("Passwords do not match!", "warning");
      setIsProcessing(false);
      return;
    }

    if (password.length < 6) {
      showToast("Password must be at least 6 characters!", "warning");
      setIsProcessing(false);
      return;
    }

    const { error } = await updatePassword(password);

    if (error) {
      showToast("There was an error resetting your password!", "error");
      setIsProcessing(false);
      return;
    }

    showToast("Password successfully reset! Redirecting...", "success");
    setIsProcessing(false);
    setTimeout(() => {
      navigate("/dashboard");
    }, 2000);
  };

  if (!isValidSession) {
    return null;
  }

  return (
    <Box
      bg="#F8FAFC"
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="md" py={10}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Image
              src={skipgiveLogo}
              alt="SkipGive"
              mx="auto"
              mb={6}
              maxW="200px"
            />
            <Heading size="xl" color="#1A202C" mb={2}>
              Reset Your Password
            </Heading>
            <Text color="#4a5568">
              Enter your new password below to secure your account.
            </Text>
          </Box>

          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            boxShadow="0 4px 6px rgba(0, 0, 0, 0.07)"
          >
            <form onSubmit={handleResetPassword}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>New Password</FormLabel>
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    focusBorderColor={colors.gold + "60"}
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Confirm Password</FormLabel>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    focusBorderColor={colors.gold + "60"}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    value={confirmPassword}
                  />
                </FormControl>

                <Box width="100%">
                  <Button
                    buttonType="primary"
                    label="Reset Password"
                    isLoading={isProcessing}
                    type="submit"
                  />
                </Box>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};
